import type {
  InvalidateQueryFilters,
  QueryClient,
  QueryObserverOptions,
  UseMutationReturnType,
} from '@tanstack/vue-query';
import {
  // oxlint-disable-next-line no-restricted-imports
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/vue-query';
import {
  appendPortfolioTree,
  copyPortfolioTree,
  createPortfolioTree,
  deletePortfolioTree,
  getPortfolioStatus,
  getOptimizerStatus,
  getPortfolioTreeNewDraft,
  getQisOptions,
  updateQisOptions,
  submitBasket,
  getUniqueBasketReferences,
} from '@/api-v2/web/portfolio-trees';
import type { Ref } from 'vue';
import { computed } from 'vue';
import type {
  IAppendPortfolioTreeParams,
  IPortfolioTree,
  IPortfolioTreeDraftParams,
  IPortfolioTreeDraftRequestBody,
  ISetPortfolioTreeParams,
} from '@/types/IPortfolioTree';
import type { RouteLocationNormalizedLoadedGeneric } from 'vue-router';
import { useRoute } from 'vue-router';
import type { VQMutationOptions } from './defineMutation';
import { defineMutation, useManagedMutation } from './defineMutation';
import { chainEnabled, unwrap } from '@/utils/queries';
import { useIndexUniqueIdentifier } from '../useCorrectIdentifier';
import { PortfolioModule } from '@/store/modules/PortfolioStore';
import useLoadingWhenEnabledQuery from './useLoadingWhenEnabledQuery';
import { getAppliedPortfolioViewMode, usePortfolioViewGroupingConfig } from '../usePortfolioViewMode';
import { PortfolioViewMode } from '@/constants/PortfolioViewMode';
import type { BasketEventStatusConstants } from '@/constants/BasketEventStatusConstants';
import { useGetDraftParams } from '@/composables/useGetDraftParams';
import type { CalculateDraftMutationParams } from '@/types/DraftCalculateMutationParams';
import type { MaybeRef } from '@vueuse/shared';
import { getReferenceCodeOrSlug } from '@/utils/portfolioTree';
import useAppMetrics from '../useAppMetrics';
import { RouteName } from '@/constants/RouteName';
import type { LocalPortfolioReferencesResponseDTO } from '@/api-v2/web/strategies/types/LocalPortfolioReferencesResponseDTO';
import type { VQQueryOptions, LoadingWhenEnabledQRT } from '@/types/VueQueryTypes';
import { ignoreRetryOn404 } from '../ignoreRetries';
import type { PortfolioStatusResponseDTO } from '@/types/PortfolioStatusResponseDTO';
import type { OptimizerStatusResponseDTO } from '@/types/OptimizerStatusResponseDTO';
import type { QisOptionsResponseDTO } from '@/types/QisOptionsResponseDTO';
import type { QisOptionsUpdateRequestDTO } from '@/types/QisOptionsUpdateRequestDTO';
import useRouteChecks from '../useRouteChecks';

export interface AppendPortfolioTreeInput {
  slug: string;
  payload: IAppendPortfolioTreeParams;
}

export interface SavePortfolioTreeInput {
  slug: string;

  /**
   * InvalidateQueryFilters is an array of objects that can be used to invalidate specific queries after the mutation is successful.
   * This is useful for cases where you want to invalidate queries based on specific conditions or parameters.
   * * @example Basket Submission Modal does not need the invalidation and we pass an empty array to avoid performance overhead
   */
  invalidateQueryFilters?: InvalidateQueryFilters[];

  /**
   * Whether the portfolio tree draft has been modified.
   * If the portfolio tree draft has been modified, we need to invalidate the queries
   * that are involved with the portfolio tree draft.
   */
  hasPortfolioTreeDraftBeenModified: boolean;
}

export type CalculateDraftMutationData = {
  newDefaultModeDraft: IPortfolioTree | null;
  newViewModeDraft: IPortfolioTree | null;
  paramsUsed: IPortfolioTreeDraftParams;
};

type InvalidateDraftQueriesFn = (args: { draftSlug: Ref<string | null> }) => Promise<void>;

const keys = {
  all: () => [{ scope: 'portfolio-tree-draft' }] as const,
  draft: ({
    slug,
    params,
    body,
  }: {
    slug: Ref<string | null | undefined>;
    params: MaybeRef<IPortfolioTreeDraftParams | undefined>;
    body: MaybeRef<IPortfolioTreeDraftRequestBody | undefined>;
  }) => [{ ...keys.all()[0], entity: 'draft', slug, params, body }] as const,
  append: (slug: Ref<string | null>, payload: Ref<IAppendPortfolioTreeParams>) =>
    [{ ...keys.all()[0], entity: 'append', slug, payload }] as const,
  references: () => [{ ...keys.all()[0], entity: 'references' }] as const,
  status: (slug: Ref<string | null>) => [{ ...keys.all()[0], entity: 'status', slug }] as const,
  optimizerStatus: (slug: Ref<string | null>) => [{ ...keys.all()[0], entity: 'optimizer-status', slug }] as const,
  qisOptions: (draftSlug: Ref<string | null>, analysisDate: Ref<string | null>) =>
    [{ ...keys.all()[0], entity: 'qis-options', draftSlug, analysisDate }] as const,
};

const invalidateBasketReferences = (queryClient: QueryClient): Promise<void> =>
  queryClient.invalidateQueries({ queryKey: keys.references() });

/**
 * IMPORTANT: 90% of use cases will not require this composable.
 * Instead you should be using `draft` returned from `useItemUnderAnalysisGlobalState`.
 *
 * You should only be using this composable when you want to query a new draft object for some reason, either with
 * a slug that's different from the `indexUniqueIdentifier`, or if you need to separate the vue-query state for some reason
 * (see what we do in `visibleDraftToUseForSidebarRows` in `PortfolioController`).
 *
 * The draft slug should be used when the endpoint expects to receive the draft portfolio.
 * A feeder portfolio will have its linked portfolios expanded with all their children visible.
 *
 * The original slug should be used when the API endpoint expects to receive the original portfolio.
 * A feeder portfolio will NOT have its linked portfolios expanded with all their children NOT visible.
 *
 * @warning
 * Original Slug should be used in all PDF and EXCEL exports.
 * For PDFs: the original slug is supplied so that the sls-chromeless project will go to a portfolio
 *   that exists and then it will generate a draft slug itself and use that to generate the PDF
 * For Excels: the original slug is supplied so that the generated excel will be used for the S3 bucket
 *   The API *MUST* expand the portfolio tree on their end so that the data is correct.
 *
 * **Portfolio Mode Data Flow and Update Process**
 *
 * DataLayer keeps two observers: `defaultModeDraft` (always DEFAULT, the edit
 * source of truth) and `visibleDraft` (DEFAULT or VIEW — what the page shows).
 * The default mode portfolio is the primary source of truth for all modifications.
 *
 * @remarks
 * Portfolio Update Workflow:
 *
 * ```
 *                     ┌──────────────────┐
 *                     │  Real Portfolio  │
 *                     │  (Data Source)   │
 *                     └────────┬─────────┘
 *                              │
 *                              ▼ derives from
 *  ┌─────────────────────────────────────────────────────────┐
 *  │                 Draft - Default Mode                    │
 *  │                                                         │
 *  │  - Serves as the master reference for all operations    │         is modified by the user, who then
 *  │  - WYSIWYG, subportfolios and strategies correspond to  │───────▶   clicks Calculate, triggering the
 *  │     the underlying saved configuration                   │           `useCalculateDraft` mutation
 *  │  - Preserves original weights of child items            │                         │
 *  │                                                         │ ◀───────────────────────│
 *  └───────────────────────────┬─────────────────────────────┘          changes are made to the underlying structure
 *                              │
 *                              │
 *                              │
 *                              │
 *                              │
 *                              │ if the portfolio has a VIEW grouping configuration stored in local storage,
 *                              │    then a VIEW draft is automatically calculated by the api and
 *                              │   automatically re-calculated whenever changes are made
 *                              │   to the default mode draft
 *                              │
 *                              │
 *                              ▼
 *       ┌─────────────────────────────────────────────┐
 *       │            Draft - View Mode                │
 *       │                                             │
 *       │  • Structure results from transforming the  │
 *       |      draft in default mode                  │
 *       │  • Custom weighting type                    │
 *       │  • Display-optimized grouping               │
 *       │  • NOT for editing                          │
 *       └─────────────────────────────────────────────┘
 * ```
 *
 * All portfolio tree updates MUST be performed using the default mode draft as
 * the source of truth, since view mode drafts use a transformed structure with
 * custom weighting that's optimized for display but not suitable for modifications.
 *
 * When changes are made to the default mode draft, the `useCalculateDraft` mutation will:
 * 1. Post the updated draft to the database to change the underlying saved configuration
 * 2. If the draft has a VIEW `groupingConfig` applied, the mutation will also fetch the new results for the
 *      portfolio with the VIEW mode applied.
 * 3. In `onSuccess`, the mutation updates the vue-query cache with both versions of the draft, which are then
 *      available via the `usePortfolioTreeDraft` composable.
 *
 * @param force - Parameter allowing you to override the default parameters for the draft query
 *                  (in reality, this is only used on the Risk and Position PDFs to enforce a specific date)
 *
 * Please note that the default placeholderData is set to `undefined`, which is the opposite of what we do for
 * most other composables. This is because we want to properly re-trigger all draft dependent calls when the draft is updated.
 */
export function usePortfolioTreeDraft({
  slug,
  force,
  options = {},
}: {
  slug: Ref<string | null | undefined>;
  force?: MaybeRef<Partial<IPortfolioTreeDraftParams> | null>;
  options?: { enabled?: MaybeRef<boolean> } & Partial<Omit<QueryObserverOptions<IPortfolioTree, Error>, 'enabled'>>;
}): LoadingWhenEnabledQRT<IPortfolioTree, Error> {
  const route = useRoute();

  const { draftParams: params } = useGetDraftParams({ route, force });
  const { viewDraftRequestBody } = usePortfolioViewGroupingConfig({
    indexUniqueIdentifier: computed(() => slug.value ?? undefined),
  });

  const body = computed((): IPortfolioTreeDraftRequestBody | undefined => {
    // Only return the view draft request body if the mode is VIEW
    if (params.value.mode === PortfolioViewMode.DEFAULT) {
      return undefined;
    }

    return viewDraftRequestBody.value;
  });

  const enabled = chainEnabled(
    options.enabled,
    computed(() => !!slug.value),
  );

  return useLoadingWhenEnabledQuery(
    useQuery<IPortfolioTree, Error>({
      queryKey: keys.draft({ slug, params, body }),
      queryFn: ({ signal }): Promise<IPortfolioTree> =>
        getPortfolioTreeNewDraft({
          originalPortfolioSlug: unwrap(slug),
          params: unwrap(params),
          data: body.value ? unwrap(body) : undefined,
          signal,
        }),
      // IMPORTANT: placeholderData is set to undefined, which is the opposite of what we do for most other composables.
      // This is because we want to properly re-trigger all draft dependent calls when the draft is updated.
      placeholderData: undefined,
      ...options,
      enabled,
    }),
    enabled,
  );
}

export function useBareBonesPortfolioTreeDraft({
  slug,
  params,
  options = {},
}: {
  slug: Ref<string | null | undefined>;
  params: IPortfolioTreeDraftParams;
  options?: { enabled?: MaybeRef<boolean> } & Partial<Omit<QueryObserverOptions<IPortfolioTree, Error>, 'enabled'>>;
}): LoadingWhenEnabledQRT<IPortfolioTree, Error> {
  const enabled = chainEnabled(
    options.enabled,
    computed(() => !!slug.value),
  );

  return useLoadingWhenEnabledQuery(
    useQuery<IPortfolioTree, Error>({
      queryKey: keys.draft({ slug, params, body: undefined }),
      queryFn: ({ signal }): Promise<IPortfolioTree> =>
        getPortfolioTreeNewDraft({
          originalPortfolioSlug: unwrap(slug),
          params,
          data: undefined,
          signal,
        }),
      // IMPORTANT: placeholderData is set to undefined, which is the opposite of what we do for most other composables.
      // This is because we want to properly re-trigger all draft dependent calls when the draft is updated.
      placeholderData: undefined,
      ...options,
      enabled,
    }),
    enabled,
  );
}

export const cacheDraftResponse = ({
  queryClient,
  draft,
  params,
  body,
}: {
  queryClient: QueryClient;
  draft: IPortfolioTree;
  params: Partial<IPortfolioTreeDraftParams>;
  body?: IPortfolioTreeDraftRequestBody;
}): void => {
  const cacheQueryKey = [
    {
      scope: 'portfolio-tree-draft',
      entity: 'draft',
      // currently all drafts and views are cached on the originalSlug
      slug: getReferenceCodeOrSlug(draft),
      params,
      body,
    },
  ];

  queryClient.setQueryData(cacheQueryKey, draft);
};

/**
 * This mutation updates the portfolio tree draft returned by `useCorrectPortfolioTree`.
 *
 * We only ever mutate the draft with the portfolio tree in default mode, so no mode needs to be passed in here.
 *
 * During this mutation, the following occur:
 * 1. We post the `masterPortfolioTreeTempRef` to the API to apply the updates that the user wishes to make against
 *    the default mode of the draft and save them in the database.
 *
 * 2. We take the result of this API call and update the vue-query cache for this default mode draft directly,
 *    since it corresponds to the result that would be fetched from the `useCorrectPortfolioTreeDraft` query.
 *    This extra fetch is not necessary, since it would be identical to the result of this mutation.
 *
 * 3. We re-trigger all the calculations dependent on this default mode draft by invalidating the queries
 *    referring to the draft by its `slug`, NOT the `originalSlug`, which is what the draft is keyed on.
 *    This takes place at the end of the mutation, i.e., after step 4 below, if applicable.
 *
 * 4. If the user applied a VIEW grouping config, then we must also fetch the new view mode draft. So we manually call
 *    `getPortfolioTreeNewDraft` and force `mode: PortfolioViewMode.VIEW`. This is one of the few places where we call
 *    the draft API in VIEW mode, alongside the initial view-first load in `DataLayer.vue`. Any changes that occur in
 *    the view draft only ever occur after a mutation of the default mode draft.
 *
 * 5. As above with the default mode draft in Step 2, we take the result of this API call and update the
 *    vue-query cache for this view mode draft directly.
 *
 * 6. As above with the default mode draft in Step 3, we re-trigger all calculations dependent on the view draft by
 *    invalidating the queries keyed on the `slug`, not the `originalSlug`.
 */
export function useCalculateDraft(): UseMutationReturnType<
  CalculateDraftMutationData,
  Error,
  CalculateDraftMutationParams,
  unknown
> {
  const indexUniqueIdentifier = useIndexUniqueIdentifier();
  const route = useRoute();

  const { draftParams: params } = useGetDraftParams({ route });
  const { hasViewGroupingConfig, viewDraftRequestBody } = usePortfolioViewGroupingConfig({
    indexUniqueIdentifier,
  });

  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      masterPortfolioTreeTempRef: tree,
      shouldRetriggerViewModeCalculations,
    }: CalculateDraftMutationParams) => {
      if (!indexUniqueIdentifier.value) {
        throw new Error('Index unique identifier is required');
      }

      const payload: ISetPortfolioTreeParams = {
        portfolioTree: { ...tree.portfolioTree, mode: PortfolioViewMode.DEFAULT },
        allocation: tree.allocation,
        isBenchmark: tree.isBenchmark,
        indexUniqueIdentifier: indexUniqueIdentifier.value,
      };

      const newDefaultModeDraft = await PortfolioModule.UpdateDraft({
        slug: tree.slug,
        payload,
      });

      /**
       * Now we have the updated default mode draft, but if we have a view mode configuration, we must re-query the
       * draft from the database, because the shape of the portfolio tree is different from the shape returned from
       * this API call.
       */
      let newViewModeDraft: IPortfolioTree | null = null;

      if (hasViewGroupingConfig.value && shouldRetriggerViewModeCalculations) {
        newViewModeDraft = await getPortfolioTreeNewDraft({
          originalPortfolioSlug: unwrap(indexUniqueIdentifier),
          params: { ...unwrap(params), mode: PortfolioViewMode.VIEW },
          data: viewDraftRequestBody.value,
        });
      }

      return {
        newDefaultModeDraft,
        newViewModeDraft,
        paramsUsed: params.value,
      };
    },
    onSuccess: (
      { newDefaultModeDraft, newViewModeDraft, paramsUsed },
      { shouldRetriggerDefaultModeCalculations, shouldRetriggerViewModeCalculations },
    ) => {
      /**
       * invalidating the queries based on the DRAFT slug, because that's what the dependent calculations are keyed on
       */
      const slugsOfDraftsWhoseCalculationsMustBeRetriggered: string[] = [];

      if (newDefaultModeDraft) {
        cacheDraftResponse({
          queryClient,
          draft: newDefaultModeDraft,
          params: { ...paramsUsed, mode: PortfolioViewMode.DEFAULT },
        });

        if (shouldRetriggerDefaultModeCalculations)
          slugsOfDraftsWhoseCalculationsMustBeRetriggered.push(newDefaultModeDraft.slug);
      }

      const appliedPortfolioViewMode = getAppliedPortfolioViewMode(newViewModeDraft);
      const hasReturnedViewDraft = appliedPortfolioViewMode === PortfolioViewMode.VIEW;

      if (newViewModeDraft && hasReturnedViewDraft) {
        cacheDraftResponse({
          queryClient,
          draft: newViewModeDraft,
          params: { ...paramsUsed, mode: PortfolioViewMode.VIEW },
          body: viewDraftRequestBody.value,
        });

        if (shouldRetriggerViewModeCalculations)
          slugsOfDraftsWhoseCalculationsMustBeRetriggered.push(newViewModeDraft.slug);
      }

      for (const slug of slugsOfDraftsWhoseCalculationsMustBeRetriggered) {
        // IMPORTANT NOTE: although it is `invalidateQueries`, you may NOT feed this function an array.
        // The reason why it is plural here is that it will fuzzy match any query matching
        // the queryKey, which in this case is the draft slug.
        queryClient.invalidateQueries({ queryKey: [{ slug }] });
      }
    },
  });
}

export const useSavePortfolioTree = (
  options: VQMutationOptions<IPortfolioTree, Error, SavePortfolioTreeInput> = {},
): UseMutationReturnType<IPortfolioTree, Error, SavePortfolioTreeInput, unknown> => {
  const queryClient = useQueryClient();

  return useManagedMutation(
    {
      mutationFn({ slug }) {
        return PortfolioModule.SavePortfolioTree({ slug });
      },
      invalidateCache() {
        return new Promise<void>((resolve) => {
          resolve();
        });
      },
      onSuccess(data, { hasPortfolioTreeDraftBeenModified, invalidateQueryFilters }) {
        /**
         * We only want to invalidate the draft for the portfolio tree that we just saved
         * if it differs at all from the previous version.
         *
         * No need to invalidate `{ queryKey: keys.all() }`, because we didn't modify all portfolios, only one.
         * No need to invalidate `{ queryKey: discoveryAllKey() }` because we have not added or removed any portfolios.
         */
        const defaultQueriesToInvalidate: InvalidateQueryFilters[] = hasPortfolioTreeDraftBeenModified
          ? [
              {
                // The draft is keyed on the original slug, hence we pass data.originalSlug
                queryKey: [{ slug: data.originalSlug }],
              },
            ]
          : [];

        const queriesToInvalidate = invalidateQueryFilters ?? defaultQueriesToInvalidate;

        return Promise.all([
          invalidateBasketReferences(queryClient),
          ...queriesToInvalidate.map((filter) => queryClient.invalidateQueries(filter)),
        ]);
      },
    },
    options,
  );
};

const getSegmentPageName = (route: RouteLocationNormalizedLoadedGeneric): string => {
  if (route.name === RouteName.FACTSHEET) return 'Factsheet';

  if (route.name === RouteName.DATA) return 'Discover';

  return 'Analytics';
};

export function useCreatePortfolioTree(
  options: VQMutationOptions<IPortfolioTree, Error, ISetPortfolioTreeParams>,
): UseMutationReturnType<IPortfolioTree, Error, ISetPortfolioTreeParams, unknown> {
  const route = useRoute();
  const { trackPortfolioCreated } = useAppMetrics();

  const queryClient = useQueryClient();

  return useManagedMutation(
    {
      mutationFn: createPortfolioTree,
      invalidateCache() {
        return new Promise<void>((resolve) => {
          resolve();
        });
      },
      onSuccess(data) {
        // We purposefully do not wait for the invalidation of these queries in `invalidateCache` -
        // it is enough to invalidate them and for them to update in the background.
        // In order to stop them from preventing a route change, use `queryClient.cancelQueries`.
        queryClient.invalidateQueries({
          queryKey: [{ scope: 'discover', entity: 'portfolios' }],
        });
        queryClient.invalidateQueries({
          queryKey: [{ scope: 'discover', entity: 'portfolio-summary' }],
        });
        invalidateBasketReferences(queryClient);

        // no need to invalidate `{ queryKey: keys.all() }` because we have not yet calculated anything
        // using this newly created portfolio tree yet

        trackPortfolioCreated({
          pageName: getSegmentPageName(route),
          portfolioId: data.portfolioId,
          valueSelected: {
            portfolioType: data.portfolioTree.type,
            portfolioSlug: data.slug,
            isBenchmark: data.isBenchmark,
          },
        });
      },
    },
    options,
  );
}

export const useDuplicatePortfolioTree = (
  options: VQMutationOptions<IPortfolioTree, Error, ISetPortfolioTreeParams & { slug: string }>,
): UseMutationReturnType<IPortfolioTree, Error, ISetPortfolioTreeParams & { slug: string }, unknown> => {
  const queryClient = useQueryClient();

  return useManagedMutation(
    {
      mutationFn: ({ slug, ...rest }) => {
        return copyPortfolioTree(slug, rest);
      },
      invalidateCache() {
        return new Promise<void>((resolve) => {
          resolve();
        });
      },
      onSuccess() {
        // We purposefully do not wait for the invalidation of these queries in `invalidateCache` -
        // it is enough to invalidate them and for them to update in the background.
        // In order to stop them from preventing a route change, use `queryClient.cancelQueries`.
        queryClient.invalidateQueries({
          queryKey: [{ scope: 'discover', entity: 'portfolios' }],
        });
        queryClient.invalidateQueries({
          queryKey: [{ scope: 'discover', entity: 'portfolio-summary' }],
        });
        invalidateBasketReferences(queryClient);

        // no need to invalidate `{ queryKey: keys.all() }` because we have not yet calculated anything
        // using this newly created portfolio tree yet
      },
    },
    options,
  );
};

/**
 * When deleting a portfolio, please always make sure to also use the
 * `useDeletePortfoliosAndAssociatedFiles` to delete the files associated with the portfolio and the tree
 * (important for portfolios that hasCustomRebalancing = true)
 * The favorites table will however not be updated to not be affected by the portfolio soft-delete/restore process
 */
export const useDeletePortfolioTreeMutation = (
  options: VQMutationOptions<void, Error, string>,
): UseMutationReturnType<void, Error, string, unknown> => {
  const queryClient = useQueryClient();

  return useManagedMutation(
    {
      mutationFn: deletePortfolioTree,
      invalidateCache() {
        return new Promise<void>((resolve) => {
          resolve();
        });
      },
      onSuccess() {
        // We purposefully do not wait for the invalidation of these queries in `invalidateCache` -
        // it is enough to invalidate them and for them to update in the background.
        // In order to stop them from preventing a route change, use `queryClient.cancelQueries`.
        queryClient.invalidateQueries({
          queryKey: [{ scope: 'discover', entity: 'portfolios' }],
        });
        queryClient.invalidateQueries({
          queryKey: [{ scope: 'discover', entity: 'portfolio-summary' }],
        });
        invalidateBasketReferences(queryClient);

        // no need to invalidate `{ queryKey: keys.all() }` because we immediately route to
        // the next portfolio, which will re-trigger the calculations anyway
      },
    },
    options,
  );
};

/**
 * Function to submit a basket and create an official snapshot/request
 * The slug is the originalSlug of master portfolio tree instead of draft slug
 * which means the platform would need to save all of the user's changes before triggering this request.
 */
export const useSubmitBasket = defineMutation({
  mutationFn: ({
    originalSlug,
    basketBusinessEvent,
  }: {
    originalSlug: string;
    invalidateQueryFilters?: InvalidateQueryFilters[];
    basketBusinessEvent?: BasketEventStatusConstants;
  }) => submitBasket(originalSlug, basketBusinessEvent !== undefined ? { basketBusinessEvent } : undefined),
  invalidateCache(client, { invalidateQueryFilters }, data) {
    if (!data) return Promise.resolve();

    const invalidateQueryFiltersToUse: InvalidateQueryFilters[] = invalidateQueryFilters ?? [
      { queryKey: [{ slug: data.originalSlug }] },
    ];

    return Promise.all([
      invalidateBasketReferences(client),
      ...invalidateQueryFiltersToUse.map((filter) => client.invalidateQueries(filter)),
    ]);
  },
});

export const useUpdateQisOptions = defineMutation({
  mutationFn: ({ draftSlug, body }: { draftSlug: string; body: QisOptionsUpdateRequestDTO }) =>
    updateQisOptions(draftSlug, body),
  invalidateCache(client, { draftSlug }) {
    return client.invalidateQueries({ queryKey: [{ draftSlug }] });
  },
});

export const useAppendPortfolioTree = (
  options: VQMutationOptions<IPortfolioTree, Error, AppendPortfolioTreeInput>,
): UseMutationReturnType<IPortfolioTree, Error, AppendPortfolioTreeInput, unknown> => {
  const queryClient = useQueryClient();
  return useManagedMutation(
    {
      mutationFn({ slug, payload }: AppendPortfolioTreeInput) {
        return appendPortfolioTree(slug, payload);
      },
      invalidateCache() {
        return new Promise<void>((resolve) => {
          resolve();
        });
      },
      onSuccess(data) {
        // no need to invalidate `{ queryKey: keys.all() }` because we just added something to a portfolio tree
        // just need to invalidate the queries involving the portfolio tree that we just added to, as below
        // no need to invalidate `{ queryKey: discoveryAllKey() }` because we have not added or removed any portfolios
        return queryClient.invalidateQueries({ queryKey: [{ slug: data.slug }] });
      },
    },
    options,
  );
};

/**
 * Invalidates the portfolio queries for the given draft slug
 * This is used to invalidate all the queries dependent on the original portfolio and draft
 */
export const useInvalidatePortfolioQueries = (): InvalidateDraftQueriesFn => {
  const indexUniqueIdentifier = useIndexUniqueIdentifier();
  const queryClient = useQueryClient();
  const invalidateDraftQueries = useInvalidateDraftQueries();

  return async ({ draftSlug }: { draftSlug: Ref<string | null> }): Promise<void> => {
    // must await the invalidation of the draft first
    await queryClient.invalidateQueries({
      queryKey: [
        {
          entity: 'draft',
          slug: indexUniqueIdentifier.value,
        },
      ],
    });

    // then once we have our new draft, we invalidate all the dependent queries
    await invalidateDraftQueries({ draftSlug });
  };
};

/**
 * Invalidates the draft queries for the given draft slug
 * This is used to invalidate the queries dependent on the draft
 */
export const useInvalidateDraftQueries = (): InvalidateDraftQueriesFn => {
  const queryClient = useQueryClient();

  return async ({ draftSlug }: { draftSlug: Ref<string | null> }): Promise<void> => {
    // invalidate all the dependent queries keyed on the current draft slug
    await queryClient.invalidateQueries({
      queryKey: [
        {
          slug: draftSlug.value,
        },
      ],
    });
  };
};

export function useUniqueBasketReferences(
  options: VQQueryOptions<LocalPortfolioReferencesResponseDTO> = {},
): LoadingWhenEnabledQRT<LocalPortfolioReferencesResponseDTO, Error> {
  const enabled = chainEnabled(options.enabled);

  return useLoadingWhenEnabledQuery(
    useQuery<LocalPortfolioReferencesResponseDTO, Error>({
      queryKey: keys.references(),
      queryFn: ({ signal }): Promise<LocalPortfolioReferencesResponseDTO> => getUniqueBasketReferences(signal),
      ...options,
      retry: ignoreRetryOn404,
    }),
    enabled,
  );
}

/**
 * Get status of basket portfolios
 *
 * @param portfolioOriginalSlug - Basket slug
 * @param options - Query options for status retrieval
 */
export function usePortfolioStatus({
  portfolioOriginalSlug,
  options,
}: {
  portfolioOriginalSlug: Ref<string | null>;
  options?: VQQueryOptions<PortfolioStatusResponseDTO>;
}): LoadingWhenEnabledQRT<PortfolioStatusResponseDTO, Error> {
  const route = useRoute();
  const { isOnPortfolioConstruction } = useRouteChecks(route);

  const enabled = chainEnabled(
    options?.enabled,
    computed(() => !!portfolioOriginalSlug.value && isOnPortfolioConstruction.value),
  );

  return useLoadingWhenEnabledQuery(
    useQuery<PortfolioStatusResponseDTO, Error>({
      queryKey: keys.status(portfolioOriginalSlug),
      queryFn: ({ signal }): Promise<PortfolioStatusResponseDTO> =>
        getPortfolioStatus(unwrap(portfolioOriginalSlug), signal),
      ...options,
      enabled,
      staleTime: 5000,
      refetchInterval: 5000, // Poll every 5 seconds
      refetchIntervalInBackground: true,
    }),
    enabled,
  );
}

/**
 * Get optimizer status of basket portfolios
 *
 * @param portfolioOriginalSlug - Basket slug
 * @param options - Query options for optimizer status retrieval
 */
export function useOptimizerStatus({
  portfolioOriginalSlug,
  options,
}: {
  portfolioOriginalSlug: Ref<string | null>;
  options?: VQQueryOptions<OptimizerStatusResponseDTO>;
}): LoadingWhenEnabledQRT<OptimizerStatusResponseDTO, Error> {
  const route = useRoute();
  const { isOnPortfolioConstruction } = useRouteChecks(route);

  const enabled = chainEnabled(
    options?.enabled,
    computed(() => !!portfolioOriginalSlug.value && isOnPortfolioConstruction.value),
  );

  return useLoadingWhenEnabledQuery(
    useQuery<OptimizerStatusResponseDTO, Error>({
      queryKey: keys.optimizerStatus(portfolioOriginalSlug),
      queryFn: ({ signal }): Promise<OptimizerStatusResponseDTO> =>
        getOptimizerStatus(unwrap(portfolioOriginalSlug), signal),
      ...options,
      enabled,
      staleTime: 5000,
      refetchInterval: 5000, // Poll every 5 seconds
      refetchIntervalInBackground: true,
    }),
    enabled,
  );
}

export function useQisOptions({
  draftSlug,
  analysisDate,
  options,
}: {
  draftSlug: Ref<string | null>;
  analysisDate: Ref<string | null>;
  options?: VQQueryOptions<QisOptionsResponseDTO>;
}): LoadingWhenEnabledQRT<QisOptionsResponseDTO, Error> {
  const enabled = chainEnabled(options?.enabled, computed(() => !!draftSlug.value && !!analysisDate.value));

  return useLoadingWhenEnabledQuery(
    useQuery<QisOptionsResponseDTO, Error>({
      queryKey: keys.qisOptions(draftSlug, analysisDate),
      queryFn: ({ signal }): Promise<QisOptionsResponseDTO> =>
        getQisOptions(unwrap(draftSlug), unwrap(analysisDate), signal),
      ...options,
      enabled,
    }),
    enabled,
  );
}
